import { useEffect, useState, useMemo } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

import { useAppSelector } from '../../store';

import { useRolePermissions, useSnackbar, useScrollSync } from '@/hooks';
import { PerformanceStatusIndicator, ErrorState, LoadingTable } from '@/components';
import {
  useGetPriceComparisonDataQuery,
  useGetPriceComparisonFiltersQuery,
  useExportPriceComparisonToExcelMutation
} from '@/services';

import {
  BrandSummaryTable,
  BrandSummaryHeader,
  StationDetailsTable,
  PriceComparisonFilters as FilterComponent,
  getSortedStation,
  getSelectedSingle,
  getSelectedMultiple,
  getFiltersNames
} from './components';

import './PriceComparisonPage.css';
import { PriceActionFilterKey, PriceActionFilters } from '@/types/common.types';
import { getActiveFilters, getFiltersAfterRemove, getUpdatedFilters } from '../utils';

function PriceComparisonPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canExport } = useRolePermissions();
  const { setSnackbarMessage } = useSnackbar();
  const isAuthorized = useAppSelector(state => state.global.isAuthorized);

  const [filters, setFilters] = useState<PriceActionFilters>({});
  const [isExpanded, setIsExpanded] = useState(true);
  const isCollapsible = isMobile;

  const { data, isLoading, isError, isSuccess, refetch } = useGetPriceComparisonFiltersQuery(filters, {
    skip: !isAuthorized,
    refetchOnMountOrArgChange: true
  });

  const isRegionalOfficeSelected = filters.regional_office;
  const isFilters = Object.keys(filters).length > 0;

  const { data: summaryData, isError: isDataError } = useGetPriceComparisonDataQuery(filters, {
    skip: !isAuthorized || !isRegionalOfficeSelected,
    refetchOnMountOrArgChange: true
  });

  const { brand_summary = [], station_details = [], fuel_types = [] } = summaryData || {};
  const performanceStatus = data?.performanceStatus;

  const [exportPriceComparisonToExcel] = useExportPriceComparisonToExcelMutation();

  useEffect(() => {
    if (isError) {
      setSnackbarMessage('Помилка завантаження даних', 'error');
    }
  }, [isError, setSnackbarMessage]);

  useEffect(() => {
    if (isDataError) {
      setSnackbarMessage('Помилка завантаження даних', 'error');
    }
  }, [isDataError, setSnackbarMessage]);

  const ActiveFilters = useMemo(() => getActiveFilters(filters), [filters]);

  const BrandSummary = useMemo(() => (isFilters ? brand_summary : []), [brand_summary, isFilters]);

  const StationDetails = useMemo(
    () => (isFilters ? getSortedStation(station_details) : []),
    [isFilters, station_details]
  );

  const RegionalOfficesOptions = useMemo(() => getFiltersNames(data, 'regional_offices'), [data]);

  const ProvincesOptions = useMemo(() => getFiltersNames(data, 'provinces', 'Всі'), [data]);

  const BrandsOptions = useMemo(() => getFiltersNames(data, 'brands', 'Всі'), [data]);

  const { headerRef: brandHeaderRef, bodyRef: brandBodyRef } = useScrollSync();
  const { headerRef: stationsHeaderRef, bodyRef: stationsBodyRef } = useScrollSync();

  const handleFilterChange = (filterKey: PriceActionFilterKey) => (value: string) => {
    const newFilters = getUpdatedFilters(filters, filterKey, value, ProvincesOptions, BrandsOptions);
    setFilters(newFilters);
  };
  const handleRemoveActiveFilter = (key: PriceActionFilterKey, value?: string) => () => {
    const newFilters = getFiltersAfterRemove(filters, key, value);
    setFilters(newFilters);
  };

  const exportPrices = async () => {
    if (!isRegionalOfficeSelected) {
      setSnackbarMessage('Оберіть регіональне відділення для експорту', 'error');
      return;
    }
    await exportPriceComparisonToExcel(filters)
      .unwrap()
      .then(() => setSnackbarMessage('Файл успішно вивантажено', 'success'))
      .catch(() => setSnackbarMessage('Помилка вивантаження файлу', 'error'));
  };

  const retryLoad = () => {
    refetch();
  };

  if (isError) {
    return (
      <div className="price-comparison-container">
        <ErrorState message="Помилка завантаження порівняння цін" onRetry={retryLoad} />
      </div>
    );
  }

  return (
    <>
      {isSuccess && performanceStatus && <PerformanceStatusIndicator performanceStatus={performanceStatus} onRefreshNeeded={retryLoad} />}
      <div className="price-comparison-container">
        {!isSuccess || isLoading ? (
          <LoadingTable message="Завантаження порівняння цін..." rows={10} columns={12} />
        ) : (
          <>
            <div className="price-comparison-sticky-header">
              <div
                className={`price-comparison-collapsible${isCollapsible ? (isExpanded ? ' expanded' : ' collapsed') : ''}`}
              >
                <div className="price-comparison-filter-section">
                  <FilterComponent
                    regionalOffices={RegionalOfficesOptions}
                    provinces={ProvincesOptions}
                    brands={BrandsOptions}
                    selectedRegionalOffice={getSelectedSingle(filters?.regional_office)}
                    selectedProvinces={getSelectedMultiple(filters?.province)}
                    selectedBrands={getSelectedMultiple(filters?.brand)}
                    activeFilters={ActiveFilters}
                    canExport={canExport}
                    isLoading={false}
                    onRemoveActiveFilter={handleRemoveActiveFilter}
                    onChangeFilter={handleFilterChange}
                    onExport={exportPrices}
                  />
                </div>
              </div>

              {isCollapsible && (
                <button className="price-comparison-toggle-bar" onClick={() => setIsExpanded(v => !v)}>
                  <span className="price-comparison-toggle-summary">
                    {!isExpanded && filters.regional_office
                      ? `${getSelectedSingle(filters.regional_office)}`
                      : isExpanded
                        ? 'Згорнути фільтри'
                        : 'Розгорнути фільтри'}
                  </span>
                  {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </button>
              )}

              {BrandSummary.length > 0 && (
                <>
                  <h2 className="price-comparison-table-title">Зведення по брендах</h2>
                  <BrandSummaryHeader fuelTypes={fuel_types} scrollRef={brandHeaderRef} />
                  <BrandSummaryTable brandSummary={BrandSummary} fuelTypes={fuel_types} scrollRef={brandBodyRef} />
                </>
              )}

              {StationDetails.length > 0 && (
                <>
                  <h2 className="price-comparison-table-title">Деталі по станціях</h2>
                  {!isMobile && (
                    <div className="price-comparison-stations-header-wrapper" ref={stationsHeaderRef}>
                      <div className="price-comparison-table">
                        <div className="price-comparison-table-header">
                          <div className="price-comparison-header-cell price-comparison-col-unicode">Юнікод</div>
                          <div className="price-comparison-header-cell price-comparison-col-division">Дивізіон</div>
                          <div className="price-comparison-header-cell price-comparison-col-region">Регіон</div>
                          <div className="price-comparison-header-cell price-comparison-col-province">Область</div>
                          <div className="price-comparison-header-cell price-comparison-col-brand">Бренд АЗК</div>
                          <div className="price-comparison-header-cell price-comparison-col-address">Адреса АЗК</div>
                          {fuel_types.map(fuel => (
                            <div
                              key={fuel.fuel_type}
                              className="price-comparison-header-cell price-comparison-fuel-header price-comparison-col-fuel"
                            >
                              <div>{fuel.fuel_type}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {isMobile && (
                    <div className="price-comparison-stations-header-wrapper" ref={stationsHeaderRef}>
                      <table className="price-comparison-table mobile-table">
                        <thead>
                          <tr>
                            <th className="price-comparison-header-cell price-comparison-col-unicode">Юнікод</th>
                            <th className="price-comparison-header-cell price-comparison-col-division">Дивізіон</th>
                            <th className="price-comparison-header-cell price-comparison-col-region">Регіон</th>
                            <th className="price-comparison-header-cell price-comparison-col-province">Область</th>
                            <th className="price-comparison-header-cell price-comparison-col-brand">Бренд АЗК</th>
                            <th className="price-comparison-header-cell price-comparison-col-address">Адреса АЗК</th>
                            {fuel_types.map(fuel => (
                              <th
                                key={fuel.fuel_type}
                                className="price-comparison-header-cell price-comparison-col-fuel"
                              >
                                {fuel.fuel_type}
                              </th>
                            ))}
                          </tr>
                        </thead>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            <StationDetailsTable
              stationDetails={StationDetails}
              fuelTypes={fuel_types}
              isMobile={isMobile}
              scrollRef={stationsBodyRef}
            />
          </>
        )}
      </div>
    </>
  );
}

export default PriceComparisonPage;
